import type { Web3TxMultiCalcSWR } from "@/hooks/tx-flow/useDepositCalculate";
import type { ResponseTxFees } from "@/hooks/tx-flow/useDepositStart";
import type { SupWeb3DepositStep, SupWeb3TxSingleVault, SupWithdrawableBalance, SupWithdrawableToken } from "@/types";
import React, { type Dispatch, type SetStateAction, useEffect } from "react";

import { useRouteState } from "@/components/Web3/Common/Route/State";
import Web3CommonTransferWrapper from "@/components/Web3/Common/Transfer/Wrapper";
import useWithdrawStart from "@/hooks/tx-flow/useWithdrawStart";

interface SupWeb3StepTransferProps {
  selectedBalance: SupWithdrawableBalance | SupWithdrawableToken;
  vaultList: SupWeb3TxSingleVault[];
  setStep: Dispatch<SetStateAction<SupWeb3DepositStep>>;
  setTransferTx: Dispatch<SetStateAction<string | undefined>>;
  setTotalGasCost: Dispatch<SetStateAction<number | undefined>>;
  setPauseRequests: Dispatch<SetStateAction<boolean>>;
  targetChainId: number;
  simulate: boolean;
  setSimulate: Dispatch<SetStateAction<boolean>>;
  setDiscountedAmount: Dispatch<SetStateAction<ResponseTxFees | undefined>>;
  calculatedRoutes: Web3TxMultiCalcSWR;
}

export default function Web3WithdrawTransfer({
  vaultList,
  targetChainId,
  setStep,
  setTransferTx,
  setTotalGasCost,
  selectedBalance,
  setPauseRequests,
  setSimulate,
  simulate,
  setDiscountedAmount,
  calculatedRoutes,
}: SupWeb3StepTransferProps) {
  const { selectedRoutes } = useRouteState();
  const { data: depositStartData, error: depositStartError, isLoading: isApiLoading } = useWithdrawStart(vaultList, calculatedRoutes, selectedRoutes);

  useEffect(() => {
    if (depositStartData?.discount_percent !== undefined && depositStartData?.discount_usd !== undefined && depositStartData?.original_fee_usd !== undefined) {
      setDiscountedAmount({
        discount_percent: depositStartData.discount_percent,
        discount_usd: depositStartData.discount_usd,
        original_fee_usd: depositStartData.original_fee_usd,
      });
    } else {
      setDiscountedAmount(undefined);
    }
  }, [depositStartData, setDiscountedAmount]);

  return (
    <Web3CommonTransferWrapper
      selectedRoutes={selectedRoutes}
      simulate={simulate}
      setSimulate={setSimulate}
      vaultList={vaultList}
      selectedBalance={selectedBalance}
      setStep={setStep}
      setTransferTx={setTransferTx}
      setTotalGasCost={setTotalGasCost}
      setPauseRequests={setPauseRequests}
      apiData={depositStartData}
      apiError={depositStartError}
      apiIsLoading={isApiLoading}
      chainId={targetChainId}
      calculatedRoutes={calculatedRoutes}
      type="withdraw"
    />
  );
}
